import { useCallback, useEffect, useRef } from "react";

/** HL-10498 counts a promo as seen once at least half of it is on screen. */
const SEEN_RATIO = 0.5;

/** Returns a ref that calls `onSeen` once per page view while `enabled` and at least half on screen. */
const useSeenOnScreen = (enabled: boolean, onSeen: () => void) => {
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const onSeenRef = useRef(onSeen);
  onSeenRef.current = onSeen;
  const onScreenRef = useRef(false);
  const seenOnPathRef = useRef<string | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const report = useCallback(() => {
    const path = window.location.pathname;
    if (!enabledRef.current || !onScreenRef.current || seenOnPathRef.current === path) return;
    seenOnPathRef.current = path;
    onSeenRef.current();
  }, []);

  const ref = useCallback(
    (element: Element | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      onScreenRef.current = false;
      // Without IntersectionObserver there is no way to tell it was seen, so nothing is sent.
      if (!element || typeof IntersectionObserver === "undefined") return;
      const observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[entries.length - 1];
          onScreenRef.current = entry.isIntersecting && entry.intersectionRatio >= SEEN_RATIO;
          report();
        },
        { threshold: SEEN_RATIO }
      );
      observerRef.current = observer;
      observer.observe(element);
    },
    [report]
  );

  // A route change, or an accordion opening, while it is already on screen is a new view.
  useEffect(report);

  return ref;
};

export default useSeenOnScreen;
